import tenantByHost from "@/config/tenantByHost.json";
import { MICROSITE_TENANTS } from "@/lib/seo/micrositeTenants";
import { resolveTenantKeyFromHost } from "@/lib/tenantKey";

type HostMap = Record<string, string>;

const hostToTenant = tenantByHost as HostMap;

const DEV_HOSTS = new Set([
  "localhost",
  "127.0.0.1",
  "dev-campus-portal-webapp.talentedge.dev",
]);

function isDevHost(host: string) {
  const h = host.trim().toLowerCase().split(":")[0];
  return DEV_HOSTS.has(h) || h.endsWith(".talentedge.dev");
}

/** Canonical production hostname (no www) from tenantByHost.json for a tenant key. */
export function resolveCanonicalHostForTenantKey(
  tenantKey: string,
): string | undefined {
  if (!tenantKey) return undefined;

  let canonical: string | undefined;
  let wwwFallback: string | undefined;

  for (const [host, key] of Object.entries(hostToTenant)) {
    if (key !== tenantKey) continue;
    const normalized = host.trim().toLowerCase();
    if (isDevHost(normalized)) continue;

    if (normalized.startsWith("www.")) {
      if (!wwwFallback) {
        wwwFallback = normalized.replace(/^www\./, "");
      }
      continue;
    }

    canonical = normalized;
    break;
  }

  return canonical || wwwFallback;
}

function stripWww(hostname: string) {
  return hostname.trim().toLowerCase().replace(/^www\./, "");
}

function isLocalHostname(hostname: string) {
  const h = stripWww(hostname);
  return h === "localhost" || h === "127.0.0.1" || isDevHost(h);
}

/**
 * Production host for microsite LSQ (tenantByHost.json).
 * On localhost/dev, uses tenant key mapping — not window.location.hostname.
 */
export function resolveMicrositeHostDomain(tenantKey?: string): string | undefined {
  const tk =
    tenantKey ||
    (typeof window !== "undefined"
      ? resolveTenantKeyFromHost(window.location.host)
      : undefined);

  if (!tk || !MICROSITE_TENANTS.has(tk)) return undefined;

  const fromTenantHost = resolveCanonicalHostForTenantKey(tk);
  if (fromTenantHost) return fromTenantHost;

  if (typeof window === "undefined") return undefined;
  const current = stripWww(window.location.hostname);
  if (isLocalHostname(current)) return undefined;
  return current;
}

/**
 * The two page URLs LSQ is sent, public https on localhost (path/query preserved).
 *
 *  - `landingPage` — `entryUrl`, the first page of the session (`getEntryPage()`), passed in
 *    because only the caller can know it. "" stays "": the current page is NOT a stand-in,
 *    it is the other field.
 *  - `platformUrl` — the page the lead is dropped on, i.e. the current one.
 */
export function buildMicrositePublicUrls(
  tenantKey?: string,
  entryUrl = "",
): {
  landingPage: string;
  platformUrl: string;
} {
  if (typeof window === "undefined") {
    return { landingPage: "", platformUrl: "" };
  }

  const hostDomain = resolveMicrositeHostDomain(tenantKey);
  if (!hostDomain || !isLocalHostname(window.location.hostname)) {
    return {
      landingPage: entryUrl,
      platformUrl: `${window.location.origin}${window.location.pathname}`,
    };
  }

  const origin = `https://${hostDomain}`;
  let landingPage = "";
  try {
    if (entryUrl) {
      const entry = new URL(entryUrl);
      landingPage = `${origin}${entry.pathname || "/"}${entry.search}`;
    }
  } catch {
    /* not a parseable URL — no landing page rather than a made-up one */
  }
  return {
    landingPage,
    platformUrl: `${origin}${window.location.pathname || "/"}`,
  };
}
